// marketConfig.js — fetches the caller's market (currency + prices + locale) from
// GET /api/market-config and formats prices. Priority model: a LOGGED-IN user
// gets their account's saved market (pinned server-side at signup / first
// login — identical view every visit, VPN or not); a LOGGED-OUT visitor is
// IP-driven (Cloudflare geo in prod, client IP-lookup fallback for local dev,
// ?geo=XX dev override). The fetch carries the auth token so the server can
// apply that precedence. Display drives what the visitor SEES; the charge uses
// the market resolved server-side at checkout.
// Falls back to India defaults if everything is unreachable.
import axios from "axios";
import { useState, useEffect } from "react";
import { getGeoCountry, applyForcedGeo } from "@/utils/geo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CFG_KEY = "pos_market_cfg";     // last resolved config, cached as a fetch-failure fallback

// The user-facing region selector was removed (market is IP-only). Its old
// localStorage override would otherwise PIN affected browsers to a stale market
// forever (it silently beat geo and even the ?geo= dev param) — heal it.
try { localStorage.removeItem("pos_market_override"); } catch { /* storage off */ }

// India defaults — the safe fallback so a failed fetch never blanks the prices.
export const FALLBACK_CONFIG = {
  market: "IN", name: "India", locale: "en-IN", job_locale: "en_IN",
  currency: "INR", symbol: "₹", doc_term: "resume",
  detected_country: null, needs_consent_banner: false,
  supported_markets: ["US", "UK", "CA", "AU", "IN"],
  prices: { one_time: 99, one_time_was: 499, pass_monthly: null, pass_annual: 2499, pass_annual_was: 4999 },
};

// Human labels for the region selector (keys match market_profile.SUPPORTED_MARKETS).
export const MARKET_LABELS = { US: "United States", UK: "United Kingdom", CA: "Canada", AU: "Australia", IN: "India" };
const MARKET_LOCALES = { US: "en-US", UK: "en-GB", CA: "en-CA", AU: "en-AU", IN: "en-IN" };

function normalizeLocale(value, fallback = "en-IN") {
  const locale = String(value || "").trim().replace("_", "-");
  try {
    return Intl.getCanonicalLocales(locale || fallback)[0] || fallback;
  } catch {
    return fallback;
  }
}

function normalizeConfig(raw) {
  const incoming = raw && typeof raw === "object" ? raw : {};
  const market = String(incoming.market || FALLBACK_CONFIG.market).trim().toUpperCase();
  const localeFallback = MARKET_LOCALES[market] || FALLBACK_CONFIG.locale;
  const locale = normalizeLocale(incoming.locale || incoming.job_locale, localeFallback);
  const jobLocale = String(
    incoming.job_locale || incoming.locale || locale.replace("-", "_"),
  ).trim();
  return {
    ...FALLBACK_CONFIG,
    ...incoming,
    market,
    name: String(incoming.name || MARKET_LABELS[market] || "").trim(),
    locale,
    job_locale: jobLocale,
    prices: { ...FALLBACK_CONFIG.prices, ...(incoming.prices || {}) },
  };
}

function readCachedCfg() {
  try {
    const s = localStorage.getItem(CFG_KEY);
    return s ? normalizeConfig(JSON.parse(s)) : null;
  }
  catch { return null; }
}
function writeCachedCfg(cfg) {
  try { localStorage.setItem(CFG_KEY, JSON.stringify(cfg)); } catch { /* storage off */ }
}

// Auth header for the config fetch, read straight from storage (this is a
// plain util, not a hook — it can't use AuthContext). Token present => the
// server answers with the account's saved market instead of live geo.
function authHeaders() {
  try {
    const t = localStorage.getItem("pos_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch { return {}; }
}

export async function fetchMarketConfig() {
  // Version-stamp the fetch: if login/logout invalidates mid-flight, this
  // response describes the WRONG identity — it must not be cached (module
  // state or localStorage) or it resurrects the wrong-currency flash on the
  // next mount. The caller's own state updates are guarded the same way.
  const version = _authVersion;
  try {
    // Server-first: in production Cloudflare places the visitor server-side
    // (CF-IPCountry), so we ASK THE SERVER and make NO third-party IP call.
    // applyForcedGeo() only applies a ?geo= dev override / this-session country
    // to the header — it never hits the network.
    const forced = applyForcedGeo();
    let res = await axios.get(`${API}/market-config`, { headers: authHeaders() });
    let cfg = normalizeConfig(res.data);
    // Only if the server couldn't place us (local dev / no CDN geo header) and we
    // have no forced ?geo= country do we fall back to a client-side IP lookup.
    if (!cfg.detected_country && !forced) {
      const cc = await getGeoCountry();          // third-party lookup, sets the header
      if (cc) {
        res = await axios.get(`${API}/market-config`, { headers: authHeaders() });
        cfg = normalizeConfig(res.data || cfg);
      }
    }
    if (version === _authVersion) {
      writeCachedCfg(cfg);        // seed the next page load -> no flash
    }
    return cfg;
  } catch {
    return readCachedCfg() || normalizeConfig(FALLBACK_CONFIG);
  }
}

// Format an integer amount in the config's currency, e.g. price(cfg, 2499) -> "₹2,499".
export function price(cfg, amount) {
  if (amount == null || cfg == null) return null;
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return null;
  const fractionDigits = Number.isInteger(numeric) ? 0 : 2;
  const formatted = new Intl.NumberFormat(
    normalizeLocale(cfg.locale, FALLBACK_CONFIG.locale),
    { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits },
  ).format(numeric);
  return `${cfg.symbol || ""}${formatted}`;
}

// Session-shared last-resolved config. `_resolved` flips once the market has
// been CONFIRMED by the server this SPA session — until then, price surfaces
// must not render a currency at all (render a placeholder), because any seed
// (cache or fallback) can be the WRONG country (VPN switch, first visit) and
// produces the ₹→$ flash. After the first resolve, subsequent pages render
// instantly from _cache with no placeholder.
let _cache = readCachedCfg();
let _resolved = false;

// Auth transitions change WHOSE market the server answers with, so login and
// logout must drop everything resolved so far and make every mounted
// useMarketConfig() re-fetch (inline login gates — Cara, the report teaser —
// don't remount the page, so a mount-only effect would keep showing the
// logged-out market). AuthContext calls this on login/register/logout.
const _authListeners = new Set();
let _authVersion = 0;
export function invalidateMarketConfig() {
  _resolved = false;
  _cache = null;
  try { localStorage.removeItem(CFG_KEY); } catch { /* storage off */ }
  _authVersion += 1;
  _authListeners.forEach((fn) => { try { fn(_authVersion); } catch { /* listener gone */ } });
}

// Pricing config for a SPECIFIC market code — e.g. the market STAMPED ON AN
// ANALYSIS, which is what its checkout will actually charge (an analysis scored
// under US charges $14 even if the visitor later appears from the UK). Using
// this for unlock CTAs keeps displayed price == charged price, always.
// Same-market reuses the session cfg; other codes fetch once and cache.
const _mktCfgCache = {};
export function usePricingForMarket(marketCode, sessionCfg, sessionReady) {
  const code = String(marketCode || sessionCfg?.market || FALLBACK_CONFIG.market).toUpperCase();
  const same = sessionCfg && String(sessionCfg.market).toUpperCase() === code;
  const [fetched, setFetched] = useState(() => ({
    code,
    cfg: _mktCfgCache[code] || null,
  }));
  useEffect(() => {
    if (same) {
      setFetched({ code, cfg: null });
      return undefined;
    }
    if (_mktCfgCache[code]) {
      setFetched({ code, cfg: _mktCfgCache[code] });
      return undefined;
    }
    // A market-code change must immediately become unresolved. Keeping the
    // prior config here briefly exposed the previous analysis's currency.
    setFetched({ code, cfg: null });
    let active = true;
    axios.get(`${API}/market-config`, { params: { country: code } })
      .then((r) => {
        const cfg = normalizeConfig(r.data);
        _mktCfgCache[code] = cfg;
        if (active) setFetched({ code, cfg });
      })
      .catch(() => { /* keep placeholder; retry next mount */ });
    return () => { active = false; };
  }, [code, same]);
  if (same) return { cfg: sessionCfg, ready: sessionReady };
  const matching = fetched.code === code ? fetched.cfg : null;
  return { cfg: matching || FALLBACK_CONFIG, ready: !!matching };
}

// useMarketConfig() — the shared hook every pricing surface uses. Returns
// { cfg, ready, label }. RULE: render prices only when `ready` is true; show a
// neutral placeholder otherwise (never a possibly-wrong currency). Display
// only; the actual charge uses the market resolved server-side at checkout.
export function useMarketConfig() {
  const [cfg, setCfg] = useState(_cache || FALLBACK_CONFIG);
  const [ready, setReady] = useState(_resolved);
  const [authVer, setAuthVer] = useState(_authVersion);
  useEffect(() => {
    const fn = (v) => setAuthVer(v);
    _authListeners.add(fn);
    return () => { _authListeners.delete(fn); };
  }, []);
  useEffect(() => {
    let active = true;
    if (!_resolved) setReady(false);   // back to placeholder while re-resolving
    const version = _authVersion;
    fetchMarketConfig().then((c) => {
      // A response that started before a login/logout describes the wrong
      // identity — never let it mark the module cache resolved.
      if (version === _authVersion) { _cache = c; _resolved = true; }
      if (active && version === _authVersion) { setCfg(c); setReady(true); }
    });
    return () => { active = false; };
  }, [authVer]);
  return { cfg, ready, label: cfg.name || "your region" };
}
